import React, { useEffect, useState } from "react";
import { withStyles, makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@material-ui/core/Paper";
import TablePaginationActions from "../../../components/pagination/tablePaginationActions";
import { Avatar, Toolbar, Typography } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { useNavigate } from "react-router-dom";
import NoContent from "../../../components/NoContent";
import EnhancedTableHead from "../../../components/Table/tableHead";
import Skeleton from "@material-ui/lab/Skeleton";
import SearchComponent from "../../../components/searchBar";

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
  },
  body: {
    fontSize: 14,
    padding: 10,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const createData = (
  title,
  thumbNail,
  status,
  author,
  postDate,
  updated,
  id
) => ({
  title,
  thumbNail,
  status,
  author,
  postDate,
  updated,
  id,
});

const useStyles = makeStyles((theme) => ({
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },

  doNotShowInMobile: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  titleSkeleton: {
    width: 400,
    height: 40,
    float: "left",
    [theme.breakpoints.down("xs")]: {
      width: 100,
    },
  },
  skeleton: {
    width: "100%",
    height: 40,
    float: "right",
  },
  paginationSkeleton: {
    width: "100%",
    height: 30,
    marginLeft: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  isClickable: {
    cursor: "pointer",
  },
}));

export default (props) => {
  const {
    data,
    isUpdating,
    headCells,
    noSearch,
    hidden,
    title,
    pagination,
    onPageChange,
    noAvatar,
  } = props;

  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const isInMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const [page, setPage] = useState(0);
  const [displayResult, setDisplayResult] = useState({
    startIndex: 0,
    endIndex: 20,
    result: [],
    searchTerm: "",
    count: 0,
  });
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("postDate");

  const rows = data.map((elt) =>
    createData(
      elt.title,
      elt.thumbNail,
      elt.status,
      elt.author,
      elt.postDate,
      elt.updated,
      elt.id
    )
  );

  useEffect(() => {
    setDisplayResult({
      ...displayResult,
      searchTerm: "",
    });
  }, [isUpdating]);
  useEffect(() => {
    const articlesData = stableSort(rows, getComparator(order, orderBy)).filter(
      (article) =>
        article.title
          .toLowerCase()
          .includes(displayResult.searchTerm.toLowerCase())
    );

    setDisplayResult({
      ...displayResult,
      result: articlesData.slice(
        displayResult.startIndex,
        displayResult.endIndex
      ),
      count: articlesData.length,
      searchTerm: isUpdating ? "" : displayResult.searchTerm,
    });
  }, [
    displayResult.startIndex,
    rows.length,
    orderBy,
    order,
    displayResult.searchTerm,
  ]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setDisplayResult((prevState) => ({
      ...prevState,
      startIndex:
        page < newPage ? prevState.startIndex + 20 : prevState.startIndex - 20,
      endIndex:
        page < newPage ? prevState.endIndex + 20 : prevState.endIndex - 20,
    }));

    setPage(newPage);
    onPageChange(newPage);
  };

  const goToPage = (url) => navigate(url);

  const onSearch = (term) =>
    setDisplayResult({ ...displayResult, searchTerm: term });

  return (
    <div className={classes.root}>
      <TableContainer component={Paper}>
        <br />
        {title && (
          <Toolbar>
            <Typography
              style={{ flex: "1 1 100%" }}
              variant="h4"
              id="tableTitle"
              component="div"
            >
              {title}
            </Typography>
          </Toolbar>
        )}
        {!noSearch && (
          <SearchComponent
            value={displayResult.searchTerm}
            placeholder="Search"
            onChange={onSearch}
          />
        )}

        <Table
          className={classes.table}
          aria-label="customized table"
          size={isInMobile ? "small" : "medium"}
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            headCells={headCells}
          />

          <TableBody>
            {displayResult.result[0] ? (
              displayResult.result.map((row, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell
                    component="th"
                    scope="row"
                    onClick={() => goToPage(`${row.id}`)}
                    className={classes.isClickable}
                  >
                    {isUpdating ? (
                      <Skeleton
                        variant="rect"
                        className={classes.titleSkeleton}
                      />
                    ) : (
                      <Grid
                        container
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={2}
                      >
                        {!noAvatar && (
                          <Grid item>
                            <Avatar
                              alt={row.title || "Article"}
                              src={row.thumbNail}
                              variant="rounded"
                              className={classes.doNotShowInMobile}
                            />
                          </Grid>
                        )}

                        <Grid item className={classes.titleText}>
                          <span>{`${row.title}`}</span>
                        </Grid>
                      </Grid>
                    )}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {isUpdating ? (
                      <Skeleton variant="rect" className={classes.skeleton} />
                    ) : (
                      `${row.status}`
                    )}
                  </StyledTableCell>
                  {hidden.includes("author") ? null : (
                    <StyledTableCell align="right">
                      {isUpdating ? (
                        <Skeleton variant="rect" className={classes.skeleton} />
                      ) : (
                        `${row.author}`
                      )}
                    </StyledTableCell>
                  )}
                  {!hidden.includes("postDate") && (
                    <StyledTableCell align="right">
                      {isUpdating ? (
                        <Skeleton variant="rect" className={classes.skeleton} />
                      ) : (
                        row.postDate
                      )}
                    </StyledTableCell>
                  )}

                  {!hidden.includes("updated") && (
                    <StyledTableCell
                      align="right"
                      className={classes.doNotShowInMobile}
                    >
                      {isUpdating ? (
                        <Skeleton variant="rect" className={classes.skeleton} />
                      ) : row.updated ? (
                        row.updated
                      ) : null}
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                {headCells.map((cell, index) => (
                  <StyledTableCell align="right" key={index}>
                    {Math.round(headCells.length / 3) === index && (
                      <NoContent title="No Content" />
                    )}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            )}
          </TableBody>
          {pagination.hasNextPage ? (
            <TableFooter>
              <TableRow>
                {isUpdating ? (
                  <Skeleton
                    variant="text"
                    className={classes.paginationSkeleton}
                  />
                ) : (
                  <TablePagination
                    colSpan={2}
                    component="div"
                    count={pagination.totalDocs || 1}
                    rowsPerPage={20}
                    rowsPerPageOptions={[]}
                    page={pagination.offset}
                    onPageChange={handleChangePage}
                    ActionsComponent={TablePaginationActions}
                  />
                )}
              </TableRow>
            </TableFooter>
          ) : null}
        </Table>
      </TableContainer>
    </div>
  );
};
